import * as THREE from 'three';
import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import { quests, type Quest, type QuestObjective, type QuestReward, type QuestStatus } from '../data/quests';
import { achievements, type Achievement } from '../data/achievements';
import { shopItems as items } from '../data/shopItems';
import type * as Colyseus from 'colyseus.js';
import type { MyRoomState } from '../schema/GameState';

interface InventoryItem {
    id: string;
    name: string;
    icon?: string;
    value?: number; // Added value for selling
}

interface GameState {
    inventory: InventoryItem[];
    addToInventory: (item: InventoryItem) => void;
    removeFromInventory: (id: string) => void;

    // Shop System
    isShopOpen: boolean;
    toggleShop: () => void;
    buyItem: (itemId: string) => void;
    sellItem: (itemId: string) => void;

    // Weather System
    weather: 'clear' | 'rain' | 'snow';
    setWeather: (w: 'clear' | 'rain' | 'snow') => void;

    // Networking
    room: Colyseus.Room<MyRoomState> | null;
    setRoom: (room: Colyseus.Room<MyRoomState> | null) => void;

    // Flags
    isXR: boolean;
    setXR: (isXR: boolean) => void;

    // Content view
    activeContent: string | null;
    setActiveContent: (contentId: string | null) => void;

    // Scene Management
    currentScene: string;
    setCurrentScene: (scene: string) => void;

    // Theme
    theme: 'medieval' | 'cyberpunk';
    setTheme: (theme: 'medieval' | 'cyberpunk') => void;

    // Dialog System
    activeDialog: { npcName: string; dialogId: string; stepId: string } | null;
    startDialog: (npcName: string, dialogId: string, stepId?: string) => void;
    advanceDialog: (stepId: string) => void;
    closeDialog: () => void;

    // Economy & Progression
    gold: number;
    xp: number;
    playerClass: 'warrior' | 'archer' | 'wizard';
    setPlayerClass: (c: 'warrior' | 'archer' | 'wizard') => void;
    addGold: (amount: number) => void;
    addXp: (amount: number) => void;

    // Quest System
    activeQuests: Quest[];
    completedQuestIds: string[];
    startQuest: (questId: string) => void;
    updateQuestProgress: (type: 'collect' | 'talk' | 'kill' | 'explore', targetId: string, amount?: number) => void;

    // Achievements
    achievements: Achievement[];
    unlockedAchievementIds: string[];
    stats: { kills: number; distance: number; coins: number; explored: string[] };
    addStat: (type: 'kill' | 'distance' | 'collect' | 'explore', amount?: number, targetId?: string) => void;

    // Projectile Pool (Fixed size for performance)
    projectilePool: { id: string; type: 'arrow' | 'magic'; active: boolean; position: THREE.Vector3; targetPoint: THREE.Vector3; speed: number; startTime: number }[];
    spawnProjectile: (type: 'arrow' | 'magic', position: THREE.Vector3, targetPoint: THREE.Vector3, speed: number) => void;
    deactivateProjectile: (id: string) => void;

    // Hit Pool (Fixed size for performance)
    hitPool: { id: string; active: boolean; position: [number, number, number]; color: string; startTime: number }[];
    spawnHit: (position: [number, number, number], color: string) => void;
    deactivateHit: (id: string) => void;

    // Audio
    audioEnabled: boolean;
    audioVolume: number;
    toggleAudio: () => void;
    setAudioVolume: (v: number) => void;

    // UI State
    isInventoryOpen: boolean;
    toggleInventory: () => void;

    // Pause Menu
    isPaused: boolean;
    setPaused: (v: boolean) => void;

    // Game Start
    gameStarted: boolean;
    setGameStarted: (v: boolean) => void;

    // Maintenance Mode
    maintenanceMode: boolean;
    setMaintenanceMode: (v: boolean) => void;

    // Cooldown State
    lastAttackTime: number;
    triggerAttack: () => void;
}

export const useStore = create<GameState>()(persist((set, get) => ({
    inventory: [],
    addToInventory: (item) => set((state) => ({ inventory: [...state.inventory, item] })),
    removeFromInventory: (id) => set((state) => ({ inventory: state.inventory.filter((i) => i.id !== id) })),

    isShopOpen: false,
    toggleShop: () => set((state) => ({ isShopOpen: !state.isShopOpen })),

    buyItem: (itemId) => set((state) => {
        // Dynamic import to avoid circular dependency issues if possible, 
        // but here we just rely on the data file being standalone.
        // We need to import shopItems inside or outside. 
        // Ideally pass item data, but ID is safer.
        // Let's assume we import shopItems at top of file.
        // Wait, we can't import easily inside `create`.
        // Let's move shopItems import to top.

        // Find item in shop list (imported below/above)
        const shopItem = (items as any[]).find(i => i.id === itemId);
        if (!shopItem) return {};

        if (state.gold >= shopItem.cost) {
            const newItem = {
                id: Math.random().toString(36).substr(2, 9),
                name: shopItem.name,
                icon: shopItem.icon,
                value: Math.floor(shopItem.cost / 2)
            };
            return {
                gold: state.gold - shopItem.cost,
                inventory: [...state.inventory, newItem]
            };
        }
        return {};
    }),

    sellItem: (itemId) => set((state) => {
        const item = state.inventory.find(i => i.id === itemId);
        if (!item) return {};

        // robust logic: if item has value, use it. if not, default 10g.
        const sellValue = item.value || 10;

        return {
            gold: state.gold + sellValue,
            inventory: state.inventory.filter(i => i.id !== itemId)
        };
    }),

    weather: 'clear',
    setWeather: (weather) => set({ weather }),

    room: null,
    setRoom: (room) => set({ room }),

    isXR: false,
    setXR: (isXR) => set({ isXR }),

    activeContent: null,
    setActiveContent: (activeContent) => set({ activeContent }),

    currentScene: 'world',
    setCurrentScene: (currentScene) => set({ currentScene }),

    theme: 'cyberpunk',
    setTheme: (theme) => set({ theme }),

    // Audio
    audioEnabled: true,
    audioVolume: 0.5,
    toggleAudio: () => set((state) => ({ audioEnabled: !state.audioEnabled })),
    setAudioVolume: (v) => set({ audioVolume: v }),

    activeDialog: null,
    startDialog: (npcName, dialogId, stepId = 'start') => set({ activeDialog: { npcName, dialogId, stepId } }),
    advanceDialog: (stepId) => set((state) => (state.activeDialog ? { activeDialog: { ...state.activeDialog, stepId } } : {})),
    closeDialog: () => set({ activeDialog: null }),

    gold: 0,
    xp: 0,
    addGold: (amount) => set((state) => ({ gold: state.gold + amount })),
    addXp: (amount) => set((state) => ({ xp: state.xp + amount })),

    activeQuests: [],
    completedQuestIds: [],
    startQuest: (questId) => set((state) => {
        if (state.activeQuests.find(q => q.id === questId) || state.completedQuestIds.includes(questId)) return {};
        const newQuest = { ...quests[questId], status: 'active' as QuestStatus }; // Clone and set active
        return { activeQuests: [...state.activeQuests, newQuest] };
    }),
    updateQuestProgress: (type, targetId, amount = 1) => set((state) => {
        const updatedQuests = state.activeQuests.map(q => {
            const updatedObjectives = q.objectives.map(obj => {
                // Match type and target
                if (obj.type === type && obj.targetId === targetId && obj.currentCount < obj.targetCount) {
                    return { ...obj, currentCount: obj.currentCount + amount };
                }
                return obj;
            });

            // Check if all objectives are complete
            const isCompleted = updatedObjectives.every(obj => obj.currentCount >= obj.targetCount);

            const newStatus: QuestStatus = isCompleted ? 'completed' : 'active';

            if (isCompleted && q.status !== 'completed') {
                // Grant Rewards
                if (q.rewards) {
                    state.addGold(q.rewards.gold);
                    state.addXp(q.rewards.xp);
                    if (q.rewards.items) {
                        q.rewards.items.forEach(itemName => {
                            state.addToInventory({ id: Math.random().toString(), name: itemName });
                        });
                    }
                }
            }

            return { ...q, objectives: updatedObjectives, status: newStatus };
        });

        // Separate completed ones
        const newlyCompleted = updatedQuests.filter(q => q.status === 'completed' && !state.completedQuestIds.includes(q.id));
        const active = updatedQuests.filter(q => q.status !== 'completed');

        if (newlyCompleted.length > 0) {
            console.log("Quest Completed!", newlyCompleted);
            // Here we could trigger a toast notification
        }

        return {
            activeQuests: active, // Move to history logic could be different
            completedQuestIds: [...state.completedQuestIds, ...newlyCompleted.map(q => q.id)]
        };
    }),

    // Achievements
    achievements: achievements, // Initialized with imported achievements
    unlockedAchievementIds: [],
    stats: { kills: 0, distance: 0, coins: 0, explored: [] },
    addStat: (type, amount = 1, targetId) => set((state) => {
        const newStats = { ...state.stats };

        if (type === 'kill') newStats.kills += amount;
        if (type === 'distance') newStats.distance += amount;
        if (type === 'collect' && targetId === 'Gold Coin') newStats.coins += amount;
        if (type === 'explore' && targetId && !newStats.explored.includes(targetId)) {
            newStats.explored = [...newStats.explored, targetId];
        }

        // Check Achievements
        const newUnlockedIds = [...state.unlockedAchievementIds];
        let changed = false;

        state.achievements.forEach(ach => { // Use state.achievements here
            if (newUnlockedIds.includes(ach.id)) return;

            let unlocked = false;
            if (ach.condition.type === 'kill_count' && newStats.kills >= ach.condition.count) unlocked = true;
            if (ach.condition.type === 'distance' && newStats.distance >= ach.condition.count) unlocked = true;
            if (ach.condition.type === 'collect' && ach.condition.targetId === 'Gold Coin' && newStats.coins >= ach.condition.count) unlocked = true;
            if (ach.condition.type === 'explore' && ach.condition.targetId && newStats.explored.includes(ach.condition.targetId)) unlocked = true;

            if (unlocked) {
                newUnlockedIds.push(ach.id);
                console.log(`🏆 Achievement Unlocked: ${ach.title}`);
                changed = true;
                // Trigger toast here
            }
        });

        return { stats: newStats, unlockedAchievementIds: newUnlockedIds };
    }),

    // Player Class
    playerClass: 'warrior',
    setPlayerClass: (c) => set({ playerClass: c }),

    // Projectile Pool (Increased to 80 for high-velocity combat)
    projectilePool: Array.from({ length: 80 }, (_, i) => ({
        id: `proj-${i}`,
        type: 'arrow',
        active: false,
        position: new THREE.Vector3(),
        targetPoint: new THREE.Vector3(),
        speed: 0,
        startTime: 0
    })),
    spawnProjectile: (type, position, targetPoint, speed) => set((state) => {
        const index = state.projectilePool.findIndex(p => !p.active);
        if (index === -1) {
            console.warn("[POOL] Projectile pool exhausted!");
            return {};
        }

        const newPool = [...state.projectilePool];
        newPool[index] = {
            ...newPool[index],
            type,
            position: position.clone(),
            targetPoint: targetPoint.clone(),
            speed,
            active: true,
            startTime: performance.now()
        };

        // Quiet monitoring (only logs if high)
        const activeCount = newPool.filter(p => p.active).length;
        if (activeCount > 60) console.warn(`[DEBUG] Projectile Pool High Load: ${activeCount}/80`);

        return { projectilePool: newPool };
    }),
    deactivateProjectile: (id) => set((state) => {
        const index = state.projectilePool.findIndex(p => p.id === id);
        if (index === -1 || !state.projectilePool[index].active) return {};

        const newPool = [...state.projectilePool];
        newPool[index] = { ...newPool[index], active: false };
        return { projectilePool: newPool };
    }),

    // Hit Pool (Increased to 60 for overlapping effects)
    hitPool: Array.from({ length: 60 }, (_, i) => ({
        id: `hit-${i}`,
        active: false,
        position: [0, 0, 0],
        color: '#ffffff',
        startTime: 0
    })),
    spawnHit: (position, color) => set((state) => {
        const index = state.hitPool.findIndex(h => !h.active);
        if (index === -1) {
            console.warn("[POOL] Hit pool exhausted!");
            return {};
        }

        const newPool = [...state.hitPool];
        newPool[index] = {
            ...newPool[index],
            active: true,
            position: [position[0], position[1], position[2]],
            color,
            startTime: performance.now()
        };

        const activeCount = newPool.filter(h => h.active).length;
        if (activeCount > 45) console.warn(`[DEBUG] Hit Pool High Load: ${activeCount}/60`);

        return { hitPool: newPool };
    }),
    deactivateHit: (id) => set((state) => {
        const index = state.hitPool.findIndex(h => h.id === id);
        if (index === -1 || !state.hitPool[index].active) return {};

        const newPool = [...state.hitPool];
        newPool[index] = { ...newPool[index], active: false };
        return { hitPool: newPool };
    }),

    isInventoryOpen: false,
    toggleInventory: () => set((state) => ({ isInventoryOpen: !state.isInventoryOpen })),

    isPaused: false,
    setPaused: (v: boolean) => set({ isPaused: v }),

    lastAttackTime: 0,
    triggerAttack: () => set({ lastAttackTime: Date.now() })
    ,
    // Show the start screen so the player can choose a class before entering.
    gameStarted: false,
    setGameStarted: (v: boolean) => set({ gameStarted: v }),

    // Maintenance Mode
    maintenanceMode: false,
    setMaintenanceMode: (v: boolean) => set({ maintenanceMode: v })
}), {
    name: 'r3f-portfolio-storage-v3', // Bump version to force reset
    partialize: (state) => ({
        activeQuests: state.activeQuests,
        completedQuestIds: state.completedQuestIds,
        unlockedAchievementIds: state.unlockedAchievementIds,
        stats: state.stats,
        inventory: state.inventory,
        gold: state.gold,
        xp: state.xp,
        theme: state.theme,
        weather: state.weather
    }),
}));
